import { Box, CircularProgress } from '@mui/material';
import { Suspense } from 'react';

import {
  useGetPortfolioAnalysisSubscriptionQuery,
  usePostPortfolioAnalysisSubscriptionMutation,
  useUpdatePortfolioAnalysisSubscriptionMutation,
} from '@/api/energy-analysis/portfolioAnalysisApiSlice';
import {
  useAddServiceToAccountMutation,
  useRemoveServiceFromAccountMutation,
} from '@/api/recalc/endpoints/accountsApiSlice';
import { useGetUnitsByAccountQuery } from '@/api/recalc/endpoints/unitsApiSlice';
import { useGetSensorsByAccountIdQuery } from '@/api/recalc/endpoints/sensorsApiSlice';
import ServiceManagementDrawer from '@/features/service-management/components/ServiceManagementDrawer';
import ManagePortfolioAnalysisSubscription from '@/features/service-management/components/portfolio-analysis/ManagePortfolioAnalysisSubscription';

const AccountDrawerContainer = ({ isOpen, onClose, account }) => {
  const [addServiceToAccount] = useAddServiceToAccountMutation();
  const [removeServiceFromAccount] = useRemoveServiceFromAccountMutation();
  const [updatePortfolioAnalysisSubscription] =
    useUpdatePortfolioAnalysisSubscriptionMutation();
  const [
    postPortfolioAnalysisSubscriptionMutation,
    { isLoading: isPortfolioAnalysisPostLoading },
  ] = usePostPortfolioAnalysisSubscriptionMutation();
  const { data: units } = useGetUnitsByAccountQuery(account.AccountId, {
    skip: !account?.AccountId,
  });
  const { data: sensors } = useGetSensorsByAccountIdQuery(account.AccountId, {
    skip: !account?.AccountId,
  });
  const {
    data: subscription,
    isLoading: subscriptionIsLoading,
    error: subscriptionError,
    isSuccess: subscriptionIsSuccess,
  } = useGetPortfolioAnalysisSubscriptionQuery(
    { recalc_account_id: account.AccountId },
    {
      skip: !account?.AccountId,
    },
  );
  const handleAddService = async (serviceId) => {
    try {
      await addServiceToAccount({
        accountId: account.AccountId,
        serviceId,
      }).unwrap();
    } catch (error) {
      console.error('Error adding service:', error);
    }
  };

  const handleRemoveService = async (serviceId) => {
    try {
      await removeServiceFromAccount({
        accountId: account.AccountId,
        serviceId,
      }).unwrap();
    } catch (error) {
      console.error('Error removing service:', error);
    }
  };

  const updatePortfolioAnalysis = async (update) => {
    try {
      await updatePortfolioAnalysisSubscription({
        recalc_account_id: account.AccountId,
        body: update,
      }).unwrap();
    } catch (error) {
      console.error('Error updating subscription', error);
    }
  };
  const createPortfolioAnalysis = async (body) => {
    try {
      await postPortfolioAnalysisSubscriptionMutation(body).unwrap();
    } catch (error) {
      console.error('Error creating subscription', error);
    }
  };
  return (
    <Suspense
      fallback={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            verticalAlign: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      }
    >
      <ServiceManagementDrawer
        account={{
          ...account,
          jsonObjects: [
            { object: account, name: 'Account' },
            { object: units, nameKey: 'Name', idKey: 'UnitId', name: 'Units' },
            {
              object: sensors,
              nameKey: 'SensorType',
              idKey: 'SensorId',
              name: 'Sensors',
            },
          ],
        }}
        open={isOpen}
        onClose={onClose}
        handleAddService={handleAddService}
        handleRemoveService={handleRemoveService}
        portfolioAnalysisManageComponent={
          <ManagePortfolioAnalysisSubscription
            updatePortfolioAnalysis={updatePortfolioAnalysis}
            account={account}
            subscription={subscription}
            subscriptionIsLoading={subscriptionIsLoading}
            subscriptionError={subscriptionError}
            subscriptionIsSuccess={subscriptionIsSuccess}
            createSubscription={createPortfolioAnalysis}
            isPortfolioAnalysisPostLoading={isPortfolioAnalysisPostLoading}
          />
        }
      />
    </Suspense>
  );
};

export default AccountDrawerContainer;
